import React, { useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Collapse } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { useSnackbar } from 'notistack'
import {
  ExpandLessRounded as UpIcon,
  ExpandMoreRounded as DownIcon,
  FileCopyRounded as FileIcon,
} from '@material-ui/icons'

import DateTime from 'luxon/src/datetime.js'

import { convertSlaTime } from '../../../utils/customMethods'

import IconButton from '../../atoms/inputs/iconButton'
import Typography from '../../atoms/display/typography'
import EscapeDrag from '../../molecules/display/escapeDrag'

import useStyles from './ConversationInformationStyle'

const isCopyItem = ['id', 'protocol', 'identifier']
const block = [
  'departmentId',
  'consumerId',
  'userId',
  'skillId',
  'skill_id',
  'preChat',
  'posChat',
  'tabName',
  'tabId',
  'closedByRole',
  'closedById',
  'closedByUser',
  'tags',
]
const usersClosedBy = ['agent', 'manager', 'system', 'consumer', 'client']
const isDate = ['createdAt', 'start', 'assignedTime', 'end']
const isTranslate = ['status']

const ConversationInformation = (props) => {
  const classes = useStyles()
  const theme = useTheme()

  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const generalInformation = useSelector((state) => state.generalInformation)
  const [expanded, setExpanded] = useState({
    0: false,
  })

  const dataInfo =
    generalInformation.conversations[props?.chatId]?.chatInfo || {}

  const handleCollapse = useCallback((index) => {
    setExpanded((expanded) => ({
      ...expanded,
      [index]: !expanded[index],
    }))
  }, [])

  const copyToClippboard = (string) => {
    navigator.clipboard.writeText(string).then(
      () => {
        enqueueSnackbar(`${t('commons.copied')}`, {
          variant: 'success',
        })
      },
      (err) => {
        console.error(t('commons.copiedError'), err)
      }
    )
  }

  const formatDate = (value) => {
    const date = new Date(value)
    const format = `D '-' t`
    const time = DateTime.fromJSDate(date).setLocale('pt-br').toFormat(format)

    return isNaN(date.getTime()) ? value : time
  }

  const renderItems = useCallback(() => {
    const items = Object.keys(dataInfo || {}).filter(
      (item) => dataInfo[item] && !block.includes(item)
    )

    if (!items.length) {
      return <div className={classes.emptySpace}>{t('items.notfound')}</div>
    }

    if (!items.includes('closedBy')) {
      items.push('closedBy')
      dataInfo['closedBy'] = dataInfo['closedByUser'] || dataInfo['closedByRole']
    }

    const getItem = (item) => {
      if (isDate.includes(item)) return formatDate(dataInfo[item])

      if (item === 'sla') return convertSlaTime(Number(dataInfo[item]))

      if (item === 'closedBy' && usersClosedBy.includes(dataInfo[item]))
        return t(`commons.${dataInfo[item]}`)

      return dataInfo[item] ?? '-'
    }

    const getTranslateItem = (item) => {
      if (item === 'status') {
        return t(`commons.status.${getItem(item)}`)
      }

      return getItem(item) ?? '-'
    }

    return items.map((item, index) => (
      <tr ket={index}>
        <td>
          <Typography variant="body1">
            {t(`commons.${item === 'status' ? 'status.label' : item}`)}
          </Typography>
        </td>
        <td>
          <div className="subcontent">
            <Typography variant="body1">
              <EscapeDrag>
                {isTranslate.includes(item)
                  ? getTranslateItem(item)
                  : getItem(item)}
              </EscapeDrag>
            </Typography>
            {isCopyItem.includes(item) && (
              <div className="copyIcon">
                <IconButton
                  color="primary"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    copyToClippboard(getItem(item))
                  }}
                >
                  <FileIcon />
                </IconButton>
              </div>
            )}
          </div>
        </td>
      </tr>
    ))
  }, [dataInfo])

  const renderContent = () => {
    return (
      <>
        <div className={classes.menuBar} onClick={() => handleCollapse(0)}>
          <Typography variant="body1">
            {t('commons.conversationInformation')}
          </Typography>
          <div className={classes.arrowContent}>
            {expanded[0] ? <UpIcon /> : <DownIcon />}
          </div>
        </div>
        <Collapse in={expanded[0]} timeout="auto" unmountOnExit>
          <div className={classes.content}>
            <table width="100%" cellPadding="10">
              <tbody>{renderItems()}</tbody>
            </table>
          </div>
        </Collapse>
      </>
    )
  }

  return renderContent()
}

export default ConversationInformation
